// Modules
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, isToday, isYesterday, isThisWeek } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// UI
import { Text } from 'components/catalyst/text';
import {
  WrenchScrewdriverIcon,
  FolderIcon,
  UserPlusIcon,
  ChatBubbleLeftIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  BellIcon,
  CheckIcon,
  TrashIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

// Model-specific icon and color configuration
const MODEL_CONFIG = {
  WorkOrder: {
    icon: WrenchScrewdriverIcon,
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400',
    borderColor: 'border-l-orange-500',
  },
  Project: {
    icon: FolderIcon,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-l-blue-500',
  },
  Lead: {
    icon: UserPlusIcon,
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-l-green-500',
  },
  Message: {
    icon: ChatBubbleLeftIcon,
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-l-purple-500',
  },
  Alert: {
    icon: ExclamationTriangleIcon,
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    borderColor: 'border-l-red-500',
  },
  Document: {
    icon: DocumentTextIcon,
    bgColor: 'bg-gray-100 dark:bg-gray-900/30',
    iconColor: 'text-gray-600 dark:text-gray-400',
    borderColor: 'border-l-gray-500',
  },
  Schedule: {
    icon: CalendarIcon,
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    borderColor: 'border-l-cyan-500',
  },
  Location: {
    icon: MapPinIcon,
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
    iconColor: 'text-pink-600 dark:text-pink-400',
    borderColor: 'border-l-pink-500',
  },
};

const DEFAULT_CONFIG = {
  icon: BellIcon,
  bgColor: 'bg-zinc-100 dark:bg-zinc-800',
  iconColor: 'text-zinc-600 dark:text-zinc-400',
  borderColor: 'border-l-zinc-500',
};

// Get configuration based on model type
const getModelConfig = (model) => {
  if (!model || typeof model !== 'string') return DEFAULT_CONFIG;
  return MODEL_CONFIG[model] || DEFAULT_CONFIG;
};

// Group notifications by date
const groupNotificationsByDate = (notifications) => {
  const groups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: [],
  };

  notifications.forEach((notification) => {
    const date = new Date(notification.createdAt);
    if (isToday(date)) {
      groups.today.push(notification);
    } else if (isYesterday(date)) {
      groups.yesterday.push(notification);
    } else if (isThisWeek(date)) {
      groups.thisWeek.push(notification);
    } else {
      groups.older.push(notification);
    }
  });

  return groups;
};

// Single notification item component
const NotificationItem = ({ notification, onMarkAsRead, onNavigate, onDelete }) => {
  const { t } = useTranslation('common');
  const { _id, relatedTo, message, read, createdAt } = notification;
  const config = getModelConfig(relatedTo?.model);
  const IconComponent = config.icon;

  return (
    <div
      className={`
        relative flex gap-3 p-3 rounded-lg border-l-4 cursor-pointer
        transition-all duration-200 hover:shadow-md
        ${read
          ? 'bg-white dark:bg-zinc-900 border-l-transparent'
          : `bg-zinc-50 dark:bg-zinc-800/50 ${config.borderColor}`
        }
      `}
      onClick={() => onNavigate(_id, relatedTo?.id, relatedTo?.model)}
    >
      {/* Unread indicator dot */}
      {!read && (
        <div className="absolute top-3 left-1 w-2 h-2 rounded-full bg-blue-500" />
      )}

      {/* Icon */}
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${config.bgColor}`}>
        <IconComponent className={`w-5 h-5 ${config.iconColor}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <Text className={`text-sm font-medium ${!read ? 'text-zinc-900 dark:text-white' : 'text-zinc-700 dark:text-zinc-300'}`}>
              {relatedTo?.model || 'Notification'}
            </Text>
            <Text className={`text-sm mt-0.5 line-clamp-2 ${!read ? 'text-zinc-700 dark:text-zinc-200' : 'text-zinc-500 dark:text-zinc-400'}`}>
              {message}
            </Text>
          </div>

          {/* Actions menu */}
          <Menu as="div" className="relative flex-shrink-0">
            <MenuButton
              className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-700"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisVerticalIcon className="w-5 h-5 text-zinc-400" />
            </MenuButton>
            <MenuItems className="absolute right-0 z-10 mt-1 w-40 origin-top-right rounded-md bg-white dark:bg-zinc-800 shadow-lg ring-1 ring-black/5 focus:outline-none">
              <div className="py-1">
                {!read && (
                  <MenuItem>
                    {({ active }) => (
                      <button
                        className={`${active ? 'bg-zinc-100 dark:bg-zinc-700' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(_id);
                        }}
                      >
                        <CheckIcon className="w-4 h-4" />
                        {t('NOTIFICATION_MARK_AS_READ')}
                      </button>
                    )}
                  </MenuItem>
                )}
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? 'bg-zinc-100 dark:bg-zinc-700' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(_id, relatedTo?.id, relatedTo?.model);
                      }}
                    >
                      <FolderIcon className="w-4 h-4" />
                      {t('NOTIFICATION_VIEW_DETAILS')}
                    </button>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? 'bg-red-50 dark:bg-red-900/20' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(_id);
                      }}
                    >
                      <TrashIcon className="w-4 h-4" />
                      {t('NOTIFICATION_DELETE')}
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>

        {/* Time */}
        <Text className="text-xs text-zinc-400 dark:text-zinc-500 mt-1.5">
          {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
        </Text>
      </div>
    </div>
  );
};

// Date group header
const DateGroupHeader = ({ title }) => (
  <div className="flex items-center gap-2 py-2">
    <Text className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">
      {title}
    </Text>
    <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
  </div>
);

// Empty state component
const EmptyState = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
        <InboxIcon className="w-8 h-8 text-zinc-400" />
      </div>
      <Text className="text-base font-medium text-zinc-700 dark:text-zinc-300 mb-1">
        {t('NOTIFICATION_EMPTY_TITLE')}
      </Text>
      <Text className="text-sm text-zinc-500 dark:text-zinc-400 text-center">
        {t('NOTIFICATION_EMPTY')}
      </Text>
    </div>
  );
};

const NotificationsList = ({
  notifications,
  onMarkAsRead,
  onDelete,
  handleCloseNotificationsModal,
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const handleNotificationClick = (notificationId, relatedId, model) => {
    handleCloseNotificationsModal();
    onMarkAsRead(notificationId, true);

    if (!relatedId) return;

    switch (model) {
      case 'Project':
        navigate(`/projects/${relatedId}`);
        break;
      case 'WorkOrder':
        navigate(`/work-orders/${relatedId}`);
        break;
      case 'Customer':
        navigate(`/customers/${relatedId}`);
        break;
      case 'Lead':
        navigate(`/leads/${relatedId}`);
        break;
      default:
        navigate(`/work-orders/${relatedId}`);
        break;
    }
  };

  if (!notifications || notifications.length === 0) {
    return <EmptyState />;
  }

  const groupedNotifications = groupNotificationsByDate(notifications);
  const hasGroups = Object.values(groupedNotifications).some(group => group.length > 0);

  if (!hasGroups) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      {groupedNotifications.today.length > 0 && (
        <div>
          <DateGroupHeader title={t('NOTIFICATION_DATE_TODAY')} />
          <div className="space-y-2">
            {groupedNotifications.today.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onDelete={onDelete}
                onNavigate={handleNotificationClick}
              />
            ))}
          </div>
        </div>
      )}

      {groupedNotifications.yesterday.length > 0 && (
        <div>
          <DateGroupHeader title={t('NOTIFICATION_DATE_YESTERDAY')} />
          <div className="space-y-2">
            {groupedNotifications.yesterday.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onDelete={onDelete}
                onNavigate={handleNotificationClick}
              />
            ))}
          </div>
        </div>
      )}

      {groupedNotifications.thisWeek.length > 0 && (
        <div>
          <DateGroupHeader title={t('NOTIFICATION_DATE_THIS_WEEK')} />
          <div className="space-y-2">
            {groupedNotifications.thisWeek.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onDelete={onDelete}
                onNavigate={handleNotificationClick}
              />
            ))}
          </div>
        </div>
      )}

      {groupedNotifications.older.length > 0 && (
        <div>
          <DateGroupHeader title={t('NOTIFICATION_DATE_OLDER')} />
          <div className="space-y-2">
            {groupedNotifications.older.map((notification) => (
              <NotificationItem
                key={notification._id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
                onDelete={onDelete}
                onNavigate={handleNotificationClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsList;
